// src/features/logs/summarize.ts
// Extracted out of LogsPage once Owner Admin's cross-guild logs view
// needed the exact same per-action-type summary logic — avoids a
// second copy-paste. One-line human-readable summary per action_type,
// based on exactly what each dashboard route actually puts in
// before_value/after_value (checked against the real route code, not
// guessed). Falls back to a generic label for anything not explicitly
// handled, so a future action_type never renders as blank.

export interface SummarizableEntry {
  action_type: string;
  target_id: string | null;
  before_value: unknown;
  after_value: unknown;
}

export function summarize(entry: SummarizableEntry): string {
  const before = entry.before_value as Record<string, unknown> | null;
  const after = entry.after_value as Record<string, unknown> | null;

  switch (entry.action_type) {
    case 'command_toggle':
      return `${after?.enabled ? 'Enabled' : 'Disabled'} /${entry.target_id}`;
    case 'role_grant':
      return `Granted ${after?.role} to <@${entry.target_id}>`;
    case 'role_revoke':
      return `Revoked ${before?.role ?? 'a role'} from <@${entry.target_id}>`;
    case 'guild_settings_update': {
      const changedKeys = after ? Object.keys(after).filter((k) => JSON.stringify((before as Record<string, unknown>)?.[k]) !== JSON.stringify(after[k])) : [];
      return changedKeys.length ? `Updated settings: ${changedKeys.join(', ')}` : 'Updated server settings';
    }
    case 'reaction_role_remove':
      return `Removed reaction role for "${before?.role_name ?? entry.target_id}"`;
    case 'market_watch_remove': // retired system, historical entries may still exist
      return `Removed market watch for ${before?.item_name ?? 'an item'}`;
    case 'clan_watch_remove':
      return `Removed clan watch for ${before?.clan_name ?? 'a clan'}`;
    case 'market_alert_create':
      return `Created market alert for ${after?.itemName ?? 'an item'}`;
    case 'market_alert_update':
      return `Updated market alert for ${(after as Record<string, unknown>)?.item_name ?? (before as Record<string, unknown>)?.item_name ?? 'an item'}`;
    case 'market_alert_remove':
      return `Removed market alert for ${before?.item_name ?? 'an item'}`;
    // Creator-side actions (creator_audit_logs, not guild_audit_logs)
    case 'log_entry_delete':
      return 'Deleted a guild log entry';
    case 'log_clear_all':
      return 'Cleared all guild logs';
    default:
      return entry.action_type;
  }
}
